/**
 * <quick-links-scroller> — YouTube-style chip carousel.
 *
 * Wraps a horizontally scrolling `.quick-links__list` and shows prev/next
 * arrow buttons only when the pills overflow. The pills scroll behind the
 * arrows (a gradient fade under each arrow sells the "going behind" look).
 * The prev arrow hides at the start of the scroll, the next arrow at the end,
 * so the first/last pill is always reachable.
 *
 * Self-contained: does not touch the shared `<quick-links>` element or its
 * toggle/carousel behaviour used by the stock section.
 */
if (!customElements.get('quick-links-scroller')) {
  class QuickLinksScroller extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('.quick-links__list');
      this.prev = this.querySelector('.quick-links__arrow--prev');
      this.next = this.querySelector('.quick-links__arrow--next');
      if (!this.list || !this.prev || !this.next) return;

      this.onScroll = this.update.bind(this);
      this.list.addEventListener('scroll', this.onScroll, { passive: true });
      this.prev.addEventListener('click', () => this.scrollByPage(-1));
      this.next.addEventListener('click', () => this.scrollByPage(1));

      this.resizeObserver = new ResizeObserver(() => this.update());
      this.resizeObserver.observe(this.list);

      this.update();
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      this.list?.removeEventListener('scroll', this.onScroll);
    }

    /** Scroll by ~80% of the visible width in the given direction (-1 / 1). */
    scrollByPage(dir) {
      const amount = Math.round(this.list.clientWidth * 0.8) * dir;
      this.list.scrollBy({ left: amount, behavior: 'smooth' });
    }

    /** Toggle each arrow based on overflow and scroll position. */
    update() {
      const { scrollLeft, scrollWidth, clientWidth } = this.list;
      // 1px slack absorbs sub-pixel rounding at the extremes.
      const overflowing = scrollWidth - clientWidth > 1;
      const atStart = scrollLeft <= 1;
      const atEnd = scrollLeft >= scrollWidth - clientWidth - 1;

      this.prev.hidden = !overflowing || atStart;
      this.next.hidden = !overflowing || atEnd;
    }
  }

  customElements.define('quick-links-scroller', QuickLinksScroller);
}
